import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { 
  Download, 
  RefreshCw, 
  AlertTriangle,
  Package,
  Truck,
  CalendarIcon,
  FileSpreadsheet,
  Scale,
  XCircle,
  DollarSign,
  Percent,
  AlertCircle
} from 'lucide-react';
import { format, subDays, subMonths, startOfWeek, startOfMonth, endOfMonth } from 'date-fns';
import { cn } from '@/lib/utils';
import { useProcurementDamageReport, useSourceNames } from '@/hooks/useProcurementDamageReport';
import { 
  exportDamageReportToCSV, 
  exportDamageReportToExcel,
  enrichRecordsWithSourceNames,
  enrichBySupplierWithNames
} from '@/services/procurementDamageReportService';
import { 
  ResponsiveContainer, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend,
  Cell
} from 'recharts';
import { formatCurrency } from '@/lib/utils';

const CHART_COLORS = ['hsl(var(--destructive))', 'hsl(var(--warning))', 'hsl(var(--primary))', 'hsl(var(--secondary))'];

const ProcurementDamageReport: React.FC = () => {
  const [startDate, setStartDate] = useState<Date>(subDays(new Date(), 30));
  const [endDate, setEndDate] = useState<Date>(new Date());
  const [dateRange, setDateRange] = useState<string>('last-30-days');
  const [activeTab, setActiveTab] = useState<string>('overview');

  const { data: reportData, isLoading, isError, error, refetch } = useProcurementDamageReport(startDate, endDate);
  
  // Lazy load source names only when user switches to supplier/detailed tabs
  const needsSourceNames = activeTab === 'by-supplier' || activeTab === 'detailed';
  const { data: sourceNames, isLoading: isLoadingNames } = useSourceNames(needsSourceNames);

  // Enrich data with source names when available
  const enrichedRecords = useMemo(() => {
    if (!reportData?.records) return [];
    if (!sourceNames) return reportData.records;
    return enrichRecordsWithSourceNames(reportData.records, sourceNames);
  }, [reportData?.records, sourceNames]);

  const enrichedBySupplier = useMemo(() => {
    if (!reportData?.bySupplier) return [];
    if (!sourceNames) return reportData.bySupplier;
    return enrichBySupplierWithNames(reportData.bySupplier, sourceNames);
  }, [reportData?.bySupplier, sourceNames]);

  const handleDateRangeChange = (range: string) => {
    setDateRange(range);
    const now = new Date();
    
    switch (range) {
      case 'today':
        setStartDate(now);
        setEndDate(now);
        break;
      case 'yesterday':
        const yesterday = subDays(now, 1);
        setStartDate(yesterday);
        setEndDate(yesterday);
        break;
      case 'last-7-days':
        setStartDate(subDays(now, 7));
        setEndDate(now);
        break;
      case 'last-30-days':
        setStartDate(subDays(now, 30));
        setEndDate(now);
        break;
      case 'this-week':
        setStartDate(startOfWeek(now, { weekStartsOn: 1 }));
        setEndDate(now);
        break;
      case 'this-month':
        setStartDate(startOfMonth(now));
        setEndDate(now);
        break;
      case 'last-month':
        const lastMonth = subMonths(now, 1);
        setStartDate(startOfMonth(lastMonth));
        setEndDate(endOfMonth(lastMonth));
        break;
      default:
        break;
    }
  };

  const handleExportCSV = () => {
    if (!enrichedRecords.length) return;
    const filename = `procurement_damage_report_${format(startDate, 'yyyyMMdd')}_${format(endDate, 'yyyyMMdd')}`;
    exportDamageReportToCSV(enrichedRecords, filename);
  };

  const handleExportExcel = () => {
    if (!reportData) return;
    const exportData = {
      ...reportData,
      records: enrichedRecords,
      bySupplier: enrichedBySupplier,
    };
    const filename = `procurement_damage_report_${format(startDate, 'yyyyMMdd')}_${format(endDate, 'yyyyMMdd')}`;
    exportDamageReportToExcel(exportData, filename, { start: startDate, end: endDate });
  };

  // Error state
  if (isError) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-2xl font-bold">Procurement Damage Report</h2>
            <p className="text-muted-foreground">Track damages recorded during quality inspection of incoming procurements</p>
          </div>
        </div>
        
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error Loading Report</AlertTitle>
          <AlertDescription className="mt-2">
            <p>Couldn't load procurement damage data. Please try again.</p>
            {error instanceof Error && (
              <p className="mt-1 text-sm opacity-80">
                Details: {error.message}
              </p>
            )}
            <Button onClick={() => refetch()} variant="outline" size="sm" className="mt-3">
              <RefreshCw className="h-4 w-4 mr-2" />
              Try Again
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading damage report data...</p>
        </div>
      </div>
    );
  }

  const summary = reportData?.summary || {
    total_net_damage_kg: 0,
    total_compensation_kg: 0,
    total_rejected_items: 0,
    total_damage_value: 0,
    total_ordered_kg: 0,
    damage_rate: 0,
  };
  
  // Ensure values are defined (handles cached data with old field names)
  const netDamageKg = summary.total_net_damage_kg ?? 0;
  const compensationKg = summary.total_compensation_kg ?? 0;
  const rejectedItems = summary.total_rejected_items ?? 0;
  const damageValue = summary.total_damage_value ?? 0;
  const damageRate = summary.damage_rate ?? 0;

  const topProducts = reportData?.byProduct?.slice(0, 10) || [];
  const topSuppliers = enrichedBySupplier?.slice(0, 10) || [];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-2xl font-bold">Procurement Damage Report</h2>
          <p className="text-muted-foreground">Track damages recorded during quality inspection of incoming procurements</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => refetch()} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={handleExportCSV} variant="outline" size="sm">
            <Download className="h-4 w-4 mr-2" />
            CSV
          </Button>
          <Button onClick={handleExportExcel} variant="outline" size="sm">
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            Excel
          </Button>
        </div>
      </div>

      {/* Date Range Selector */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Report Period</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4 items-center">
            <Select value={dateRange} onValueChange={handleDateRangeChange}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Select date range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="yesterday">Yesterday</SelectItem>
                <SelectItem value="last-7-days">Last 7 Days</SelectItem>
                <SelectItem value="last-30-days">Last 30 Days</SelectItem>
                <SelectItem value="this-week">This Week</SelectItem>
                <SelectItem value="this-month">This Month</SelectItem>
                <SelectItem value="last-month">Last Month</SelectItem>
                <SelectItem value="custom">Custom Range</SelectItem>
              </SelectContent>
            </Select>

            {dateRange === 'custom' && (
              <div className="flex gap-2 items-center">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="gap-2">
                      <CalendarIcon className="h-4 w-4" />
                      {format(startDate, 'MMM dd, yyyy')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={startDate}
                      onSelect={(date) => date && setStartDate(date)}
                      initialFocus
                      className="pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
                <span className="text-muted-foreground">to</span>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="gap-2">
                      <CalendarIcon className="h-4 w-4" />
                      {format(endDate, 'MMM dd, yyyy')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={endDate}
                      onSelect={(date) => date && setEndDate(date)}
                      initialFocus
                      className="pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
              </div>
            )}

            <div className="ml-auto text-sm text-muted-foreground">
              Showing data from {format(startDate, 'MMM dd, yyyy')} to {format(endDate, 'MMM dd, yyyy')}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary KPI Cards */}
      <div className="grid gap-4 md:grid-cols-5">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Net Damage</p>
                <p className="text-2xl font-bold text-destructive">{netDamageKg.toFixed(1)} kg</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-destructive" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Compensation</p>
                <p className="text-2xl font-bold text-warning">{compensationKg.toFixed(1)} kg</p>
              </div>
              <Scale className="h-8 w-8 text-warning" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Rejected Items</p>
                <p className="text-2xl font-bold">{rejectedItems}</p>
              </div>
              <XCircle className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Damage Value</p>
                <p className="text-2xl font-bold text-destructive">{formatCurrency(damageValue)}</p>
              </div>
              <DollarSign className="h-8 w-8 text-destructive" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Damage Rate</p>
                <p className="text-2xl font-bold">{damageRate.toFixed(2)}%</p>
              </div>
              <Percent className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="by-product">By Product</TabsTrigger>
          <TabsTrigger value="by-supplier">By Supplier</TabsTrigger>
          <TabsTrigger value="detailed">Detailed Records</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          {/* Trend Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Damage Trend</CardTitle>
              <CardDescription>Daily damage amounts over the selected period</CardDescription>
            </CardHeader>
            <CardContent>
              {reportData?.trendData && reportData.trendData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={reportData.trendData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="date" 
                      tickFormatter={(value) => format(new Date(value), 'MMM dd')}
                    />
                    <YAxis />
                    <Tooltip 
                      labelFormatter={(value) => format(new Date(value), 'MMM dd, yyyy')}
                      formatter={(value: number) => [`${value.toFixed(2)} kg`, '']}
                    />
                    <Legend />
                    <Bar dataKey="net_damage_kg" fill="hsl(var(--destructive))" name="Net Damage (kg)" />
                    <Bar dataKey="compensation_kg" fill="hsl(var(--warning))" name="Compensation (kg)" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                  No damage data for the selected period
                </div>
              )}
            </CardContent>
          </Card>

          {/* Side by Side Charts */}
          <div className="grid gap-4 md:grid-cols-2">
            {/* Top Damaged Products */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="h-5 w-5" />
                  Top Damaged Products
                </CardTitle>
              </CardHeader>
              <CardContent>
                {topProducts.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={topProducts} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis 
                        dataKey="product_name" 
                        type="category" 
                        width={100}
                        tick={{ fontSize: 12 }}
                      />
                      <Tooltip formatter={(value: number) => [`${value.toFixed(2)} kg`, 'Net Damage']} />
                      <Bar dataKey="total_net_damage_kg" fill="hsl(var(--destructive))" name="Net Damage (kg)">
                        {topProducts.map((_, index) => (
                          <Cell key={`cell-${index}`} fill={`hsl(var(--destructive) / ${1 - index * 0.08})`} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                    No data available
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Damage by Supplier */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Truck className="h-5 w-5" />
                  Damage by Supplier/Agent
                </CardTitle>
              </CardHeader>
              <CardContent>
                {topSuppliers.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={topSuppliers}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="supplier_name" 
                        angle={-45} 
                        textAnchor="end" 
                        height={80}
                        tick={{ fontSize: 11 }}
                      />
                      <YAxis />
                      <Tooltip formatter={(value: number) => [`${value.toFixed(2)} kg`, 'Net Damage']} />
                      <Bar dataKey="total_net_damage_kg" fill="hsl(var(--primary))" name="Net Damage (kg)" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                    No data available
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="by-product" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Damage by Product</CardTitle>
              <CardDescription>Aggregated damage data for each product</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Product</TableHead>
                    <TableHead className="text-right">Net Damage (kg)</TableHead>
                    <TableHead className="text-right">Compensation (kg)</TableHead>
                    <TableHead className="text-right">Ordered (kg)</TableHead>
                    <TableHead className="text-right">Rejected</TableHead>
                    <TableHead className="text-right">Damage Value</TableHead>
                    <TableHead className="text-right">Damage %</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reportData?.byProduct?.map((product) => (
                    <TableRow key={product.product_name}>
                      <TableCell className="font-medium">{product.product_name}</TableCell>
                      <TableCell className="text-right text-destructive font-semibold">
                        {product.total_net_damage_kg.toFixed(2)}
                      </TableCell>
                      <TableCell className="text-right">{product.total_compensation_kg.toFixed(2)}</TableCell>
                      <TableCell className="text-right">{product.total_ordered_kg.toFixed(2)}</TableCell>
                      <TableCell className="text-right">
                        {product.rejected_count > 0 && (
                          <Badge variant="destructive">{product.rejected_count}</Badge>
                        )}
                        {product.rejected_count === 0 && '-'}
                      </TableCell>
                      <TableCell className="text-right">{formatCurrency(product.damage_value)}</TableCell>
                      <TableCell className="text-right">
                        <span className={cn(
                          product.damage_percentage > 10 ? 'text-destructive font-semibold' :
                          product.damage_percentage > 5 ? 'text-warning' : ''
                        )}>
                          {product.damage_percentage.toFixed(2)}%
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                  {(!reportData?.byProduct || reportData.byProduct.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                        No damage records found for the selected period
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="by-supplier" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Damage by Supplier/Agent</CardTitle>
              <CardDescription>Aggregated damage data for each supplier or agent</CardDescription>
            </CardHeader>
            <CardContent>
              {isLoadingNames && (
                <div className="flex items-center gap-2 mb-4 text-muted-foreground">
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  Loading supplier names...
                </div>
              )}
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Supplier/Agent</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">Records</TableHead>
                    <TableHead className="text-right">Net Damage (kg)</TableHead>
                    <TableHead className="text-right">Compensation (kg)</TableHead>
                    <TableHead className="text-right">Rejections</TableHead>
                    <TableHead className="text-right">Damage Rate</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {enrichedBySupplier?.map((supplier, index) => (
                    <TableRow key={`${supplier.source_id}-${index}`}>
                      <TableCell className="font-medium">{supplier.supplier_name}</TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {supplier.source_type === 'agent' ? 'Agent' : 
                           supplier.source_type === 'farm' ? 'Farm' :
                           supplier.source_type === 'harvest' ? 'Harvest' : 'Supplier'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">{supplier.procurement_count}</TableCell>
                      <TableCell className="text-right text-destructive font-semibold">
                        {supplier.total_net_damage_kg.toFixed(2)}
                      </TableCell>
                      <TableCell className="text-right">{supplier.total_compensation_kg.toFixed(2)}</TableCell>
                      <TableCell className="text-right">
                        {supplier.rejected_count > 0 && (
                          <Badge variant="destructive">{supplier.rejected_count}</Badge>
                        )}
                        {supplier.rejected_count === 0 && '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        <span className={cn(
                          supplier.damage_rate > 10 ? 'text-destructive font-semibold' :
                          supplier.damage_rate > 5 ? 'text-warning' : ''
                        )}>
                          {supplier.damage_rate.toFixed(2)}%
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                  {(!enrichedBySupplier || enrichedBySupplier.length === 0) && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                        No damage records found for the selected period
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="detailed" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Detailed Records</CardTitle>
                <CardDescription>
                  All damage records for the selected period ({enrichedRecords?.length || 0} records)
                </CardDescription>
              </div>
              <Button onClick={handleExportCSV} variant="outline" size="sm">
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </Button>
            </CardHeader>
            <CardContent>
              {isLoadingNames && (
                <div className="flex items-center gap-2 mb-4 text-muted-foreground">
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  Loading supplier names...
                </div>
              )}
              <div className="max-h-[600px] overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Product</TableHead>
                      <TableHead className="text-right">Ordered</TableHead>
                      <TableHead className="text-right">Net Damage</TableHead>
                      <TableHead className="text-right">Compensation</TableHead>
                      <TableHead className="text-right">Damage Value</TableHead>
                      <TableHead>Rejected</TableHead>
                      <TableHead>Source</TableHead>
                      <TableHead>Supplier/Agent</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {enrichedRecords?.map((record) => (
                      <TableRow key={record.id}>
                        <TableCell>{format(new Date(record.inspection_date), 'MMM dd, yyyy')}</TableCell>
                        <TableCell className="font-medium">{record.product_name}</TableCell>
                        <TableCell className="text-right">{record.ordered_quantity} kg</TableCell>
                        <TableCell className="text-right text-destructive font-semibold">
                          {record.net_damage_kg.toFixed(2)} kg
                        </TableCell>
                        <TableCell className="text-right">{record.compensation_kg.toFixed(2)} kg</TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(record.net_damage_kg * record.price_per_unit)}
                        </TableCell>
                        <TableCell>
                          {record.is_rejected ? (
                            <Badge variant="destructive">Rejected</Badge>
                          ) : (
                            <Badge variant="outline">Accepted</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary">
                            {record.source_type === 'agent' ? 'Agent' : 
                             record.source_type === 'farm' ? 'Farm' :
                             record.source_type === 'harvest' ? 'Harvest' : 'Supplier'}
                          </Badge>
                        </TableCell>
                        <TableCell>{record.source_name || 'Unknown'}</TableCell>
                      </TableRow>
                    ))}
                    {(!enrichedRecords || enrichedRecords.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center text-muted-foreground py-8">
                          No damage records found for the selected period
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ProcurementDamageReport;
